// src/components/LeadsPage.jsx
// KEY CHANGE: Accepts `openLeadId` and `openLeadData` props.
// When Jarvis navigates here with a specific lead, that card auto-opens.
// Parent (App.jsx) passes:
//   <LeadsPage openLeadId={id} openLeadData={lead} onLeadOpened={() => setOpenLeadId(null)} />

import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';
// P0.31 31.B.6 — one archive confirm for every entity.
import { confirmArchive } from '../lib/archiveConfirm';
import { useProfile } from '../context/UserProfileContext';
import { DIGITALME_ALIGNMENT, TASK_TYPES, TASK_PRIORITIES } from '../lib/leadSignals';
import './LeadsPage.css';
import DataTransferMenu from './DataTransferMenu';

function splitLeadTitle(title) {
  if (!title) return { account: 'Other', description: '' };
  if (title.includes(' — ')) { const idx = title.indexOf(' — '); return { account: title.slice(0, idx).trim(), description: title.slice(idx + 3).trim() }; }
  if (title.includes(' - '))  { const idx = title.indexOf(' - ');  return { account: title.slice(0, idx).trim(), description: title.slice(idx + 3).trim() }; }
  return { account: 'Other', description: title };
}

function parseStructuredTasks(nextActionsText) {
  if (!nextActionsText) return [];
  const lines = nextActionsText.split('\n').filter(l => l.startsWith('TASK||'));
  return lines.slice(0, 5).map((line, i) => { const parts = line.split('||'); return { _id: i, name: parts[1] || '', taskType: parts[2] || 'Follow-up', priority: parts[3] || 'Do Today' }; });
}

function SignalDetailCard({ sig, dateObserved }) {
  const [expanded, setExpanded] = useState(false);
  // The pitch is a function of the company name so this copy is not one
  // tenant's sales material baked into shared code (B40).
  const { companyName } = useProfile();
  const info = DIGITALME_ALIGNMENT[sig];
  const formattedDate = dateObserved ? new Date(dateObserved.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  return (
    <div className={`signal-card signal-card-clickable ${expanded ? 'signal-card-expanded' : ''}`} onClick={() => setExpanded(e => !e)}>
      <div className="signal-card-header">
        <span className="signal-icon">{info?.icon || '🔔'}</span>
        <strong>{info?.label || sig}</strong>
        <div className="signal-card-meta-group">
          <span className="signal-card-meta">✦ Detected by Selma</span>
          {formattedDate && <span className="signal-card-date">📅 {formattedDate}</span>}
        </div>
        <span className="signal-expand-chevron">{expanded ? '▾' : '▸'}</span>
      </div>
      {expanded && info && (
        <div className="signal-card-detail">
          <p className="signal-card-body">{info.pitch(companyName)}</p>
          <div className="signal-card-services">{info.services.map((svc, j) => <span key={j} className="alignment-service-tag">{svc}</span>)}</div>
        </div>
      )}
    </div>
  );
}

function TaskRow({ task, leadId, leadTitle, accountId, onCreated, onDismiss }) {
  const [name, setName]         = useState(task.name);
  const [taskType, setTaskType] = useState(task.taskType);
  const [priority, setPriority] = useState(task.priority);
  const [dueDate, setDueDate]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [created, setCreated]   = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const response = await apiFetch('/api/tasks/create', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accountId, name, taskType, priority, dueDate: dueDate || null, status: 'Approved', notes: `Lead: ${leadTitle}` }) });
      if (response.ok) { setCreated(true); if (onCreated) onCreated(); }
    } catch (err) { console.error('Task create error:', err); }
    finally { setSaving(false); }
  };

  if (created) return <div className="task-row task-row-done"><span className="task-row-check">✅</span><span className="task-row-name-done">{name}</span><span className="task-row-created-label">Created</span></div>;

  return (
    <div className="task-row">
      <button className="task-row-dismiss" onClick={onDismiss} title="Dismiss">✕</button>
      <input className="task-row-name" value={name} onChange={e => setName(e.target.value)} placeholder="Task name" />
      <select className="task-row-select" value={taskType} onChange={e => setTaskType(e.target.value)}>{TASK_TYPES.map(t => <option key={t}>{t}</option>)}</select>
      <select className="task-row-select task-row-priority" value={priority} onChange={e => setPriority(e.target.value)}>{TASK_PRIORITIES.map(p => <option key={p}>{p}</option>)}</select>
      <input type="date" className="task-row-date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
      <button className="task-row-btn" onClick={handleCreate} disabled={saving || !name.trim()}>{saving ? '...' : '+ Task'}</button>
    </div>
  );
}

// ── LeadDetailModal — exported so Jarvis can also import it if needed ─────────
export function LeadDetailModal({ lead, isArchived, onClose, onConvert, onArchive, onUnarchive, onTaskCreated }) {
  // The tab was the literal "DigitalMe Fit". UserProfileContext's own header says
  // nothing in the UI hard-codes a tenant's name; this and LeadsTab were the two
  // places that still did, so a second tenant saw a competitor's name on its own
  // leads. companyName falls back to 'Your Company'.
  const { companyName } = useProfile();
  const [activeSection, setActiveSection]   = useState('overview');
  const [taskCreatedCount, setTaskCreatedCount] = useState(0);
  const [visibleTasks, setVisibleTasks]     = useState(() => parseStructuredTasks(lead.nextActions));
  const [signalDateMap, setSignalDateMap]   = useState({});
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const signals            = lead.aiSignals || [];
  const hasStructuredTasks = visibleTasks.length > 0;

  useEffect(() => {
    const accountId = lead.account || lead.accountId;
    if (!accountId || signals.length === 0) return;
    apiFetch(`/api/signals/by-account?accountId=${accountId}`).then(r => r.json()).then(data => {
      if (data.success && data.signals) {
        const map = {};
        data.signals.forEach(s => { if (s.name) map[s.name] = s.dateObserved || null; });
        setSignalDateMap(map);
      }
    }).catch(() => {});
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTaskCreated = () => { setTaskCreatedCount(c => c + 1); if (onTaskCreated) onTaskCreated(); };

  return (
    <div className="lead-modal-overlay" onClick={onClose}>
      <div className="lead-modal" onClick={e => e.stopPropagation()}>
        <div className="lead-modal-header">
          <div className="lead-modal-title-row">
            <div className="lead-modal-badges">
              <span className={`lead-priority lead-priority-${(lead.priority || 'medium').toLowerCase()}`}>{lead.priority}</span>
              <span className="lead-status-badge">{lead.status}</span>
              {lead.dealSize && <span className="lead-deal-badge">{lead.dealSize}</span>}
            </div>
            <button className="lead-modal-close" onClick={onClose}>✕</button>
          </div>
          <h2 className="lead-modal-title">{lead.leadTitle}</h2>
          {signals.length > 0 && (
            <div className="lead-modal-signals">
              {signals.map((sig, i) => <span key={i} className="lead-signal-badge-lg">{DIGITALME_ALIGNMENT[sig]?.icon || '🔔'} {sig}</span>)}
            </div>
          )}
          <div className="lead-modal-nav">
            {['overview', 'signals', 'alignment', 'tasks'].map(s => (
              <button key={s} className={`lead-modal-nav-btn ${activeSection === s ? 'active' : ''}`} onClick={() => setActiveSection(s)}>
                {s === 'overview' && '📋 Overview'}{s === 'signals' && `🔔 Signals (${signals.length})`}{s === 'alignment' && `🎯 ${companyName} Fit`}{s === 'tasks' && `✅ Tasks${hasStructuredTasks ? ` (${visibleTasks.length})` : ''}`}
              </button>
            ))}
          </div>
        </div>

        <div className="lead-modal-body">
          {activeSection === 'overview' && (
            <div className="lead-modal-section">
              {lead.opportunityHypothesis && <div className="lead-detail-block"><h4>Opportunity Hypothesis</h4><p>{lead.opportunityHypothesis}</p></div>}
              {lead.companyOverview       && <div className="lead-detail-block"><h4>Opportunity Overview</h4><pre className="lead-detail-pre">{lead.companyOverview}</pre></div>}
              {lead.executiveIntelligence && <div className="lead-detail-block"><h4>Target Executive</h4><pre className="lead-detail-pre">{lead.executiveIntelligence}</pre></div>}
              {lead.technologyLandscape   && <div className="lead-detail-block"><h4>Affected Technology</h4><pre className="lead-detail-pre">{lead.technologyLandscape}</pre></div>}
              {!lead.opportunityHypothesis && !lead.companyOverview && !lead.executiveIntelligence && <p className="lead-empty-note">Run research on this account to populate detailed intelligence.</p>}
            </div>
          )}
          {activeSection === 'signals' && (
            <div className="lead-modal-section">
              {signals.length === 0 ? <p className="lead-empty-note">No AI signals tagged for this lead.</p>
                : signals.map((sig, i) => <SignalDetailCard key={i} sig={sig} dateObserved={signalDateMap[sig]} />)}
            </div>
          )}
          {activeSection === 'alignment' && (
            <div className="lead-modal-section">
              {signals.length === 0 ? <p className="lead-empty-note">Tag AI signals to see DigitalMe service alignment.</p> : (
                <>
                  <div className="alignment-intro"><p>Based on the signals detected, here's how DigitalMe's capabilities align:</p></div>
                  {signals.map((sig, i) => { const info = DIGITALME_ALIGNMENT[sig]; if (!info) return null; return (
                    <div key={i} className="alignment-card">
                      <div className="alignment-card-header"><span>{info.icon}</span><strong>{info.label}</strong></div>
                      <div className="alignment-services">{info.services.map((svc, j) => <span key={j} className="alignment-service-tag">{svc}</span>)}</div>
                      <p className="alignment-pitch">{info.pitch(companyName)}</p>
                    </div>
                  );})}
                </>
              )}
            </div>
          )}
          {activeSection === 'tasks' && (
            <div className="lead-modal-section">
              {taskCreatedCount > 0 && <div className="task-saved-banner">✅ {taskCreatedCount} task{taskCreatedCount > 1 ? 's' : ''} added to your dashboard</div>}
              {hasStructuredTasks ? (
                <div className="task-rows-container">
                  <p className="task-rows-intro">Edit any field, then click <strong>+ Task</strong> to create it.</p>
                  <div className="task-rows-header"><span className="task-col-dismiss"></span><span className="task-col-name">Task</span><span className="task-col-type">Type</span><span className="task-col-priority">Priority</span><span className="task-col-date">Due Date</span><span className="task-col-action"></span></div>
                  {visibleTasks.map(task => <TaskRow key={task._id} task={task} leadId={lead.id} leadTitle={lead.leadTitle} accountId={lead.accountId || lead.account} onCreated={handleTaskCreated} onDismiss={() => setVisibleTasks(prev => prev.filter(t => t._id !== task._id))} />)}
                  {visibleTasks.length === 0 && <p className="lead-empty-note">All suggestions dismissed.</p>}
                </div>
              ) : (
                <div className="task-legacy">
                  {lead.nextActions && <div className="lead-detail-block"><h4>Suggested Next Actions</h4><pre className="lead-detail-pre">{lead.nextActions}</pre></div>}
                  <p className="task-note">Re-run research on this account to get one-click task creation.</p>
                </div>
              )}
              <p className="task-note">Tasks appear on your Dashboard and Tasks tab.</p>
            </div>
          )}
        </div>

        <div className="lead-modal-footer">
          {isArchived ? (
            <button className="btn-unarchive-modal" onClick={() => { onUnarchive(lead); onClose(); }}>↩ Restore Lead</button>
          ) : lead.convertedOpportunity ? (
            <button className="btn-view-opportunity">✓ View Opportunity →</button>
          ) : (
            <>
              {confirmConvert ? (
                <>
                  <button className="btn-convert-modal btn-confirm" onClick={() => { onConvert(lead); onClose(); }}>Confirm convert?</button>
                  <button className="btn-archive-modal" onClick={() => setConfirmConvert(false)}>Cancel</button>
                </>
              ) : confirmArchive ? (
                <>
                  <button className="btn-archive-modal btn-confirm" onClick={() => { onArchive(lead); onClose(); }}>Confirm archive?</button>
                  <button className="btn-convert-modal" onClick={() => setConfirmArchive(false)}>Cancel</button>
                </>
              ) : (
                <>
                  <button className="btn-convert-modal" onClick={() => setConfirmConvert(true)}>Convert to Opportunity</button>
                  <button className="btn-archive-modal"  onClick={() => setConfirmArchive(true)}>Archive</button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CreateLeadModal ──────────────────────────────────────────────────────────
function CreateLeadModal({ onClose, onSave }) {
  const [form, setForm]     = useState({ title: '', priority: 'Medium', status: 'Identified', hypothesis: '' });
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');

  useEffect(() => {
    apiFetch('/api/accounts/list')
      .then(r => r.json())
      .then(d => { if (d.success) setAccounts(d.accounts || []); })
      .catch(() => {});
  }, []);

  const filteredAccounts = accounts.filter(a =>
    a.accountName?.toLowerCase().includes(accountSearch.toLowerCase())
  ).slice(0, 8);

  const handleSave = async () => {
    if (!form.title.trim()) { alert('Lead title is required'); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="lead-modal-overlay" onClick={onClose}>
      <div className="lead-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="lead-modal-header">
          <h2>New Lead</h2>
          <button className="lead-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="lead-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Lead Title *</label>
            <input
              type="text"
              className="search-input"
              placeholder="e.g. Acme Corp — Core Platform Migration"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              style={{ width: '100%' }}
              autoFocus
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Format: Account Name — Opportunity Signal</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Priority</label>
              <select
                className="search-input"
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                style={{ width: '100%' }}
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Status</label>
              <select
                className="search-input"
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                style={{ width: '100%' }}
              >
                <option value="Identified">Identified</option>
                <option value="Under Review">Under Review</option>
                <option value="Verified">Verified</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Account (optional)</label>
            <input
              type="text"
              className="search-input"
              placeholder="Search accounts..."
              value={accountSearch || (form.accountName || '')}
              onChange={e => {
                setAccountSearch(e.target.value);
                setForm(f => ({ ...f, accountId: null, accountName: '' }));
              }}
              style={{ width: '100%' }}
            />
            {accountSearch && filteredAccounts.length > 0 && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4, background: 'var(--card-bg)', maxHeight: 180, overflowY: 'auto' }}>
                {filteredAccounts.map(a => (
                  <div
                    key={a.id}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => {
                      setForm(f => ({ ...f, accountId: a.id, accountName: a.accountName }));
                      setAccountSearch('');
                    }}
                  >
                    {a.accountName}
                  </div>
                ))}
              </div>
            )}
            {form.accountName && (
              <div style={{ fontSize: 12, color: 'var(--success)', marginTop: 4 }}>✓ {form.accountName}</div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Opportunity Hypothesis</label>
            <textarea
              className="search-input"
              placeholder="What's the opportunity? Why now?"
              value={form.hypothesis}
              onChange={e => setForm(f => ({ ...f, hypothesis: e.target.value }))}
              rows={3}
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        </div>

        <div className="lead-modal-footer">
          <button className="btn-convert-modal" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating...' : 'Create Lead'}
          </button>
          <button className="btn-archive-modal" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// Main LeadsPage
//   openLeadId   — Notion page ID to auto-open
//   openLeadData — Full lead object from Jarvis briefing (avoids extra API call)
//   onLeadOpened — Callback to clear openLeadId in App.jsx after opening
// ════════════════════════════════════════════════════════════════════════════════
export default function LeadsPage({ openLeadId, openLeadData, onLeadOpened, onNavigate }) {
  const [leads, setLeads]                       = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [search, setSearch]                     = useState('');
  const [filterPriority, setFilterPriority]     = useState('all');
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [selectedLead, setSelectedLead]         = useState(null);
  const [showArchived, setShowArchived]         = useState(false);
  const [showCreateModal, setShowCreateModal]   = useState(false);
  const openHandledRef                          = useRef(false);

  useEffect(() => { fetchLeads(); }, []);

  // ── Auto-open a specific lead when navigated here from Jarvis ───────────────
  useEffect(() => {
    if (!openLeadId || openHandledRef.current) return;

    // Try to find in loaded leads first
    const match = leads.find(l => l.id === openLeadId);
    if (match) {
      openHandledRef.current = true;
      const { account } = splitLeadTitle(match.leadTitle);
      setExpandedAccounts(prev => ({ ...prev, [account]: true }));
      setSelectedLead(match);
      if (onLeadOpened) onLeadOpened();
      return;
    }

    // If leads loaded but no match, use briefing data directly (no extra API call)
    if (!loading && openLeadData) {
      openHandledRef.current = true;
      const syntheticLead = {
        id:                    openLeadData.id,
        leadTitle:             openLeadData.leadTitle || openLeadData.hypothesis || 'Untitled opportunity',
        priority:              openLeadData.priority  || 'Medium',
        status:                openLeadData.status    || 'Identified',
        dealSize:              openLeadData.dealSize  || '',
        aiSignals:             openLeadData.aiSignals || [],
        opportunityHypothesis: openLeadData.opportunityHypothesis || openLeadData.hypothesis || '',
        companyOverview:       openLeadData.companyOverview   || '',
        executiveIntelligence: openLeadData.executiveTarget   || '',
        accountId:             openLeadData.accountId || null,
        account:               openLeadData.accountId || null,
      };
      setSelectedLead(syntheticLead);
      if (onLeadOpened) onLeadOpened();
    }
  }, [openLeadId, openLeadData, leads, loading, onLeadOpened]);

  // Reset handled flag when a new openLeadId arrives
  useEffect(() => { openHandledRef.current = false; }, [openLeadId]);

  // P0.31 31.B.5 — `archived`, not a status string.
  //
  // This passed `status=Archived`, which stopped matching anything when migration
  // 0035 moved archived leads onto `deleted_at`. The view went silently empty and
  // 380 leads became unreachable from the UI. The endpoint now takes `archived=1`.
  async function fetchLeads(wantArchived) {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (wantArchived) params.set('archived', '1');
      const res  = await apiFetch(`/api/leads/list${params.toString() ? '?' + params.toString() : ''}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch');
      setLeads(data.leads || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  // P0.31 31.B.5 — archived is `deleted_at`, not a status value. These three
  // filters tested `status === 'Archived'`, which 0035 emptied.
  const activeLeads   = leads.filter(l => !l.deletedAt);
  const archivedLeads = leads.filter(l => !!l.deletedAt);

  const filtered = leads.filter(l => {
    if (!showArchived && l.deletedAt) return false;
    if (showArchived  && !l.deletedAt) return false;
    if (filterPriority !== 'all' && l.priority !== filterPriority) return false;
    if (search && !(l.leadTitle || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const grouped = filtered.reduce((acc, lead) => {
    const { account } = splitLeadTitle(lead.leadTitle);
    if (!acc[account]) acc[account] = [];
    acc[account].push(lead);
    return acc;
  }, {});

  const accountNames  = Object.keys(grouped).sort();
  const highCount     = activeLeads.filter(l => l.priority === 'High').length;
  const verifiedCount = activeLeads.filter(l => l.status === 'Verified').length;
  const accountCount  = [...new Set(activeLeads.map(l => splitLeadTitle(l.leadTitle).account).filter(a => a !== 'Other'))].length;

  function isExpanded(name) { return expandedAccounts[name] !== false; }
  function toggleAccount(name) { setExpandedAccounts(prev => ({ ...prev, [name]: !isExpanded(name) })); }

  const convertToOpportunity = async (lead) => {
    try {
      const res = await apiFetch('/api/leads/convert-to-opportunity', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ leadId: lead.id }) });
      if (!res.ok) throw new Error('Failed to convert');
      setLeads(prev => prev.filter(l => l.id !== lead.id));
    } catch (err) { alert('❌ ' + err.message); }
  };

  const archiveLead = async (lead) => {
    // P0.31 31.B.6 — the shared confirm, so the wording cannot drift between
    // this page and SignalsPage. Archiving a lead used to happen with no
    // confirmation at all.
    if (!confirmArchive({
      entity: 'lead',
      name: lead.leadTitle || lead.title,
      keeps: 'Nothing is deleted, and the account keeps its other leads.',
    })) return;
    try {
      await apiFetch('/api/leads/archive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ leadId: lead.id }) });
      setLeads(prev => prev.filter(l => l.id !== lead.id));
    } catch (err) { alert('❌ ' + err.message); }
  };

  const unarchiveLead = async (lead) => {
    try {
      await apiFetch('/api/leads/unarchive', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ leadId: lead.id }) });
      setLeads(prev => prev.filter(l => l.id !== lead.id));
    } catch (err) { console.error(err); }
  };

  const toggleArchivedView = () => {
    const goingArchived = !showArchived;
    setShowArchived(goingArchived);
    fetchLeads(goingArchived);
  };

  const createLead = async (formData) => {
    try {
      const res = await apiFetch('/api/leads/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leads: [{
            title:                 formData.title,
            status:                formData.status || 'Identified',
            priority:              formData.priority || 'Medium',
            opportunityHypothesis: formData.hypothesis || '',
            accountId:             formData.accountId || null,
            source:                'Manual',
          }]
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to create lead');
      setShowCreateModal(false);
      fetchLeads();
    } catch (err) { alert('❌ ' + err.message); }
  };

  return (
    <div className="leads-page">
      <div className="leads-header">
        <div className="leads-header-left">
          <div className="leads-title-row">
            <h1 className="leads-title">Leads</h1>
            {activeLeads.length > 0 && <span className="leads-count">{activeLeads.length}</span>}
          </div>
          <div className="leads-stats">
            <span className="stat-chip stat-accounts">🏢 {accountCount} accounts</span>
            <span className="stat-chip stat-high">{highCount} high priority</span>
            <span className="stat-chip stat-verified">{verifiedCount} verified</span>
          </div>
        </div>
        <div className="leads-header-right">
          <DataTransferMenu entity="leads" label="Leads" onImported={fetchLeads} />
          <button className="btn-create-lead" onClick={() => setShowCreateModal(true)}>+ New Lead</button>
          {archivedLeads.length > 0 && (
            <button className="btn-toggle-archive" onClick={toggleArchivedView}>
              {showArchived ? `◀ Active (${activeLeads.length})` : `▶ Archived (${archivedLeads.length})`}
            </button>
          )}
          <button className="btn-refresh" onClick={() => fetchLeads()}>↻ Refresh</button>
        </div>
      </div>

      <div className="leads-filters">
        <input className="search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..." />
        <select className="filter-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="all">All Priorities</option>
          <option>High</option><option>Medium</option><option>Low</option>
        </select>
      </div>

      {loading ? (
        <div className="leads-loading">Loading leads...</div>
      ) : error ? (
        <div className="leads-loading" style={{ color: 'var(--dm-red)' }}>⚠️ {error} <button onClick={() => fetchLeads()} style={{ marginLeft: 8, cursor: 'pointer' }}>Retry</button></div>
      ) : filtered.length === 0 ? (
        <div className="leads-empty">
          {showArchived ? <p>No archived leads.</p> : <><p>No active leads.</p><p className="leads-empty-hint">Run research on an account to identify opportunities.</p></>}
        </div>
      ) : (
        <div className="leads-accounts">
          {accountNames.map(accountName => {
            const accountLeads = grouped[accountName];
            const expanded     = isExpanded(accountName);
            const acctHigh     = accountLeads.filter(l => l.priority === 'High').length;
            return (
              <div key={accountName} className="account-group">
                <div className="account-group-header" onClick={() => toggleAccount(accountName)}>
                  <div className="account-group-left">
                    <div className="account-avatar">{accountName.charAt(0).toUpperCase()}</div>
                    <span className="account-name">{accountName}</span>
                    <span className="leads-count-badge">{accountLeads.length}</span>
                    {acctHigh > 0 && <span className="account-high-badge">{acctHigh} high</span>}
                  </div>
                  <span className="account-chevron">{expanded ? '▾' : '▸'}</span>
                </div>
                {expanded && (
                  <div className="leads-grid">
                    {accountLeads.map(lead => {
                      const { description } = splitLeadTitle(lead.leadTitle);
                      return (
                        <div key={lead.id}
                          className={`lead-card lead-card-clickable ${showArchived ? 'lead-archived' : `lead-${(lead.status||'').toLowerCase().replace(/\s+/g,'-')}`}`}
                          onClick={() => setSelectedLead(lead)}>
                          <div className="lead-header">
                            <h4>{description || lead.leadTitle}</h4>
                            <span className={`lead-priority lead-priority-${(lead.priority||'medium').toLowerCase()}`}>{lead.priority}</span>
                          </div>
                          <div className="lead-meta">
                            <span className="lead-status">{lead.status}</span>
                            {lead.dealSize && <span className="lead-deal-size">{lead.dealSize}</span>}
                          </div>
                          {lead.aiSignals?.length > 0 && (
                            <div className="lead-signals">
                              {lead.aiSignals.map((signal, idx) => <span key={idx} className="lead-signal-badge">{DIGITALME_ALIGNMENT[signal]?.icon || '🔔'} {signal}</span>)}
                            </div>
                          )}
                          {lead.opportunityHypothesis && <p className="lead-hypothesis">{lead.opportunityHypothesis}</p>}
                          <div className="lead-card-footer">
                            <div className="lead-ai-actions" onClick={e => e.stopPropagation()}>
                              <button
                                className="lead-ai-btn lead-ai-btn--evaluate"
                                title="Evaluate this lead with Victor + Selma — pursue or table decision"
                                onClick={e => {
                                  e.stopPropagation();
                                  onNavigate && onNavigate('floor', null, {
                                    type: 'lead',
                                    accountId: lead.accountId || null,
                                    accountName: splitLeadTitle(lead.leadTitle).account,
                                    leadIds: [lead.id],
                                    leadHypothesis: lead.opportunityHypothesis || lead.leadTitle || '',
                                    leadSignals: lead.aiSignals || [],
                                    leadPriority: lead.priority || '',
                                    returnTo: 'leads',
                                  });
                                }}
                              >⚡ Evaluate</button>
                              <button
                                className="lead-ai-btn lead-ai-btn--research"
                                title="Research this account with Selma"
                                onClick={e => { e.stopPropagation(); onNavigate && onNavigate('floor', null, { message: `Selma, research ${splitLeadTitle(lead.leadTitle).account} — ${lead.opportunityHypothesis || lead.leadTitle}. Find recent signals, key executives, technology initiatives, and any trigger events that create an opening for DigitalMe.`, returnTo: 'leads' }); }}
                              >🔭 Research</button>
                              <button
                                className="lead-ai-btn lead-ai-btn--outreach"
                                title="Draft outreach with Cyrano"
                                onClick={e => { e.stopPropagation(); onNavigate && onNavigate('floor', null, { message: `Cyrano, draft a Signal-Led outreach email to ${splitLeadTitle(lead.leadTitle).account}. Signal: ${(lead.aiSignals||[]).join(', ') || 'opportunity identified'}. ${lead.opportunityHypothesis || ''} Use the signal as the opening hook — not a generic intro.`, returnTo: 'leads' }); }}
                              >✍️ Outreach</button>
                            </div>
                            {lead.convertedOpportunity && <span className="lead-converted-badge">✓ Converted</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          isArchived={showArchived}
          onClose={() => setSelectedLead(null)}
          onConvert={convertToOpportunity}
          onArchive={archiveLead}
          onUnarchive={unarchiveLead}
          onTaskCreated={fetchLeads}
        />
      )}

      {showCreateModal && (
        <CreateLeadModal
          onClose={() => setShowCreateModal(false)}
          onSave={createLead}
        />
      )}
    </div>
  );
}
